/**
 * <strong>Purpose:</strong> <br>
 * Provides the ability to prevent the drop down portion of a split style button from<br>
 * being disabled, when the entire widget is disabled.<br>
 *
 * <strong>Contract:</strong> <br>
 * This mixin is dependent upon: <br>
 * {@link module:mw-form/mixins/property/DisabledMixin}<br>
 * {@link module:mw-form/mixins/property/IconMixin}<br>
 * {@link module:mw-form/mixins/property/TextMixin}<br>
 * {@link module:mw-form/mixins/property/DropDownButtonArrowModeMixin}<br>
 * {@link module:mw-form/mixins/property/DropDownButtonIconAlignmentMixin}<br>
 * {@link module:mw-form/mixins/property/DropDownButtonSizeMixin}<br>
 *
 * @module mw-form/mixins/property/DropDownNeverDisabledMixin
 *
 * @copyright Copyright 2016-2018 The MathWorks, Inc.
 */

define([
        "dojo/_base/declare",
        "dojo/dom-geometry",
        "mw-form/mixins/property/DisabledMixin",
        "mw-form/mixins/property/IconMixin",
        "mw-form/mixins/property/TextMixin",
        "mw-form/mixins/property/DropDownButtonArrowModeMixin",
        "mw-form/mixins/property/DropDownButtonIconAlignmentMixin",
        "mw-form/mixins/property/DropDownButtonSizeMixin"
    ],
    function (declare, domGeometry, DisabledMixin, IconMixin, TextMixin, DropDownButtonArrowModeMixin,
              DropDownButtonIconAlignmentMixin, DropDownButtonSizeMixin) {
        var DropDownNeverDisabledMixin = declare(null, {

            dropDownNeverDisabled: false,

            // Property to add additional nodes other than focusNode for which mnemonicKeyNavDirection attribute is
            // necessary for navigation
            mnemonicKeyNavDirectionNodes: [],

            postCreate: function () {
                this.inherited(arguments);

                this.domNode.classList.add("mwDropDownNeverDisabledMixin");

                // When the default value is falsy, the setter will not be triggered during the widget's construction phase
                // We need to call the setter explicitly
                this.set("dropDownNeverDisabled", this.get("dropDownNeverDisabled"));

                // creating a bound function so that the event handler could be removed later
                this._buttonNodeInteractionHandler = this._buttonNodeInteractionHandler.bind(this);
                this._addDisableListener(this.buttonNode, this._buttonNodeInteractionHandler);

                // ButtonNode should be assigned mnemonicKeyNavDirection attribute as it receives focus separately,
                // in addition to the focusNode
                this.mnemonicKeyNavDirectionNodes.push(this.buttonNode);

                // Assign a role of button to buttonNode
                this.buttonNode.setAttribute("role", "button");
            },

            destroy: function () {
                if (this.domNode) {
                    this._removeDisableListener(this.buttonNode, this._buttonNodeInteractionHandler);
                }                
                this.inherited(arguments);
            },

            _buttonNodeInteractionHandler: function (e) {
                if (this.get("disabled")) {
                    if (this.get("dropDownNeverDisabled")) {
                        this._disabledInteraction(e);
                    }
                }
            },

            _disableInteractionHandler: function (e) {
                if (this.get("disabled")) {
                    if (!this.get("dropDownNeverDisabled")) {
                        this._disabledInteraction(e);
                    }
                }
            },

            _setDropDownNeverDisabledAttr: function (value) {
                if (typeof value !== "boolean") {
                    throw new Error("'dropDownNeverDisabled' property expects a boolean!");
                }

                this._set("dropDownNeverDisabled", value);

                // Set styles to allow for different behavior based on the value of the property
                if (value) {
                    this.domNode.classList.add("mwDropDownNeverDisabledTrue");
                } else {
                    this.domNode.classList.remove("mwDropDownNeverDisabledTrue");
                }

                this._setAriaDisabled();
                this._setTabIndexOnNode(this.focusNode);
            },

            _setTabIndexOnNode: function (node) {
                if (this.get("disabled")) {
                    this.buttonNode.removeAttribute("tabIndex");
                    if (this.get("dropDownNeverDisabled")) {
                        node.setAttribute("tabIndex", "0");
                    } else {
                        node.removeAttribute("tabIndex");
                    }
                } else {
                    node.setAttribute("tabIndex", "0");
                    this.buttonNode.setAttribute("tabIndex", "0");
                }
            }
        });

        DropDownNeverDisabledMixin._dependencies = [
            {mixin: DisabledMixin, orderDependent: true},
            {mixin: IconMixin, orderDependent: true},
            {mixin: TextMixin, orderDependent: true},
            {mixin: DropDownButtonArrowModeMixin, orderDependent: true},
            {mixin: DropDownButtonIconAlignmentMixin, orderDependent: true},
            {mixin: DropDownButtonSizeMixin, orderDependent: true}
        ];

        return DropDownNeverDisabledMixin;
    });